/**
 * Composer 행 그리드 사진 선언(`DataSourceFieldMap.photoAnalysis`/`syncTo`)의 **실행 순수 로직**.
 *
 * 선언 규약 정본은 shared `RowPhotoAnalysisConfig`/`RowFieldSyncConfig` JSDoc(결정 2026-09-07
 * 추기 4 — 사진 UI의 잠재위험요인 섹션 흡수). 여기는 도메인 지식 0 — 소스 키·필드명은 전부
 * 템플릿 선언에서 오고, 이 모듈은 선언을 실행만 한다(React/API 비의존 — 훅
 * `useComposerPhotoAnalysis`가 배선).
 *
 * 트리거 채널: 행 그리드의 사진 업로드·체크 변경은 반드시 그리드 onChange(행 배열)를 거쳐
 * 호스트 handleChange(sourceKey, 행 배열)로 올라온다. 그래서 rc에 알림 채널을 새로 뚫지 않고,
 * 호스트가 이전/다음 행 배열을 대조해 전이를 찾는다. 조립 prefill·편집 로드는 setValues 직접
 * 경로라 이 대조를 타지 않는다(자동 분석·전파가 서버 시드에 발화하지 않는 구조적 보장).
 *
 * 파생 행(deriveTo)은 **훅 소유 투영**이다: 업로드가 만들고(upsert), 원 행들의 fills 편집을
 * 단방향으로 따라가며(projectDerivedRows — 본문 사진 카드가 항상 현재 입력을 보이게), 원 행
 * 삭제·사진 삭제 시 함께 사라진다. 사용자가 파생 행을 직접 편집하는 채널은 없다.
 */
import type {
  DataSourceDef,
  DataSourceFieldMap,
  RowFieldSyncConfig,
  RowPhotoAnalysisConfig,
} from '@safetyhub/shared';

/** 수집된 사진 분석 규칙 1건 — 선언 필드(field)=업로드 URL 행 필드. */
export interface PhotoAnalysisRule {
  /** 선언이 속한 데이터 소스 키(행 그리드의 content 키) */
  sourceKey: string;
  /** 업로드 URL이 실리는 행 필드(선언 field) */
  urlField: string;
  config: RowPhotoAnalysisConfig;
}

/** 수집된 체크 전파 규칙 1건. */
export interface RowSyncRule {
  sourceKey: string;
  /** 체크 값이 실리는 행 필드(선언 field) */
  field: string;
  config: RowFieldSyncConfig;
}

/** 분석 결과 항목(호스트 API 응답의 최소 형상). */
export interface PhotoAnalysisFinding {
  description: string;
  measures: string[];
}

function asRows(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
}

function strOf(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** dataSources 선언에서 photoAnalysis 규칙 수집 — widget:'photo' 필드만 유효. */
export function collectPhotoAnalysisRules(
  dataSources: DataSourceDef[] | undefined,
): PhotoAnalysisRule[] {
  const rules: PhotoAnalysisRule[] = [];
  for (const def of dataSources ?? []) {
    for (const f of def.fields) {
      if (f.widget === 'photo' && f.photoAnalysis) {
        rules.push({ sourceKey: def.key, urlField: f.field, config: f.photoAnalysis });
      }
    }
  }
  return rules;
}

/** dataSources 선언에서 syncTo(체크 전파) 규칙 수집. */
export function collectRowSyncRules(dataSources: DataSourceDef[] | undefined): RowSyncRule[] {
  const rules: RowSyncRule[] = [];
  for (const def of dataSources ?? []) {
    for (const f of def.fields) {
      if (f.syncTo) rules.push({ sourceKey: def.key, field: f.field, config: f.syncTo });
    }
  }
  return rules;
}

/** 업로드 전이 1건 — 그 행의 URL 필드가 이전 값과 달라졌다(빈 값 → 값 포함 교체). */
export interface PhotoUploadTransition {
  rowId: string;
  url: string;
  /** 전이 시점의 행 객체(덮어쓰기 확인·토글 판정용) */
  row: Record<string, unknown>;
}

/**
 * 이전/다음 행 배열 대조로 업로드 전이를 찾는다 — rowId가 있는 행만 대상(추가 행은
 * createBlankRow가 uuid를 부여). 점검 유래 행은 URL이 처음부터 실려 오고 변하지 않아
 * 전이가 생기지 않는다(사용자 업로드만 잡힌다).
 */
export function detectPhotoUploads(
  prevValue: unknown,
  nextValue: unknown,
  urlField: string,
): PhotoUploadTransition[] {
  const prevById = new Map<string, Record<string, unknown>>();
  for (const row of asRows(prevValue)) {
    if (typeof row.rowId === 'string') prevById.set(row.rowId, row);
  }
  const out: PhotoUploadTransition[] = [];
  for (const row of asRows(nextValue)) {
    const rowId = typeof row.rowId === 'string' ? row.rowId : undefined;
    if (!rowId) continue;
    const url = strOf(row[urlField]);
    if (url === '') continue;
    const prev = prevById.get(rowId);
    const prevUrl = prev ? strOf(prev[urlField]) : '';
    if (url !== prevUrl) out.push({ rowId, url, row });
  }
  return out;
}

/** 행별 자동 분석 토글 판정 — 선언 없음/값 미정의(구 데이터·기본 시드 전)는 켜짐, false류만 꺼짐. */
export function isAnalysisEnabled(
  row: Record<string, unknown>,
  enabledField: string | undefined,
): boolean {
  if (!enabledField) return true;
  const v = row[enabledField];
  if (v === undefined || v === null || v === '') return true;
  return v !== false && v !== 'false';
}

/** 행에 이미 채워진 값이 있는가 — 덮어쓰기 확인 게이트(fills 대상 필드 중 하나라도 비지 않음). */
export function hasExistingRowContent(
  row: Record<string, unknown>,
  fills: RowPhotoAnalysisConfig['fills'],
): boolean {
  if (fills.description && strOf(row[fills.description]) !== '') return true;
  if (fills.measuresJoined && strOf(row[fills.measuresJoined]) !== '') return true;
  return false;
}

/** 결과 항목 1건 → fills 필드 패치(빈 대책은 빈 문자열 — 편집 칸이 빈 채 남는다). */
export function findingFillPatch(
  finding: PhotoAnalysisFinding,
  fills: RowPhotoAnalysisConfig['fills'],
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  if (fills.description) patch[fills.description] = finding.description;
  if (fills.measuresJoined) {
    patch[fills.measuresJoined] = finding.measures.filter((m) => strOf(m) !== '').join(', ');
  }
  return patch;
}

/** 대상 소스 선언 필드의 빈 행(rc createBlankRow 규약 거울 — defaultValue 우선, 목록형 []). */
function blankRowFor(fields: DataSourceFieldMap[] | undefined): Record<string, unknown> {
  const row: Record<string, unknown> = { rowId: crypto.randomUUID() };
  for (const f of fields ?? []) {
    row[f.field] =
      f.defaultValue !== undefined
        ? f.defaultValue
        : f.widget === 'textarea-list' || f.widget === 'text-list'
          ? []
          : f.widget === 'date-range'
            ? {}
            : '';
  }
  return row;
}

/** spill 계산 결과 — rows=추가할 새 행, limitDropped=rowLimit 컷으로 잘린 건수. */
export interface SpillRowsResult {
  rows: Record<string, unknown>[];
  limitDropped: number;
}

/**
 * 둘째 이후 분석 결과 → 같은 소스 새 행 계산(순수). 새 행은 선언 빈값 골격 + fills 매핑 +
 * 같은 사진 URL(urlField)을 싣는다(사진 1장에 위험요인 여럿 — 구 hazards 그룹 동형).
 * rowLimit 존중 — 잘린 건수는 호출측이 안내한다(조용한 드랍 금지).
 */
export function buildSpillRows(
  restFindings: PhotoAnalysisFinding[],
  url: string,
  rule: PhotoAnalysisRule,
  targetDef: DataSourceDef | undefined,
  currentRowCount: number,
): SpillRowsResult {
  const limit = targetDef?.rowLimit;
  const limited =
    typeof limit === 'number' && Number.isInteger(limit) && limit > 0 ? limit : undefined;
  const rows: Record<string, unknown>[] = [];
  let limitDropped = 0;
  for (const f of restFindings) {
    if (limited !== undefined && currentRowCount + rows.length >= limited) {
      limitDropped += 1;
      continue;
    }
    const row = blankRowFor(targetDef?.fields);
    Object.assign(row, findingFillPatch(f, rule.config.fills));
    row[rule.urlField] = url;
    rows.push(row);
  }
  return { rows, limitDropped };
}

/**
 * 소스의 rowId 행 1개를 갱신한 새 values(불변) — 행이 사라졌으면(작업 중 행 삭제) 원본 그대로
 * 반환해 stale 커밋을 버린다(rc runPhotoUpload isAlive와 같은 취지).
 */
export function updateSourceRow(
  values: Record<string, unknown>,
  sourceKey: string,
  rowId: string,
  update: (row: Record<string, unknown>) => Record<string, unknown>,
): Record<string, unknown> {
  const rows = asRows(values[sourceKey]);
  if (!rows.some((r) => r.rowId === rowId)) return values;
  return {
    ...values,
    [sourceKey]: rows.map((r) => (r.rowId === rowId ? update(r) : r)),
  };
}

/**
 * 파생 행 upsert(순수) — 업로드 직후 호출: 원 행(sourceRowId)의 파생 행이 있으면 URL 갱신,
 * 없으면 선언 골격으로 생성. 자식 항목(findings)은 이후 `projectDerivedRows`가 투영한다.
 */
export function upsertDerivedPhotoRow(
  values: Record<string, unknown>,
  rule: PhotoAnalysisRule,
  targetDef: DataSourceDef | undefined,
  sourceRowId: string,
  url: string,
): Record<string, unknown> {
  const deriveTo = rule.config.deriveTo;
  if (!deriveTo) return values;
  const rows = asRows(values[deriveTo.sourceKey]);
  const idx = rows.findIndex((r) => r[deriveTo.sourceRowField] === sourceRowId);
  if (idx >= 0) {
    const next = rows.map((r, i) => (i === idx ? { ...r, [deriveTo.urlField]: url } : r));
    return { ...values, [deriveTo.sourceKey]: next };
  }
  const row = blankRowFor(targetDef?.fields);
  row[deriveTo.urlField] = url;
  row[deriveTo.sourceRowField] = sourceRowId;
  if (deriveTo.findingsField) row[deriveTo.findingsField] = [];
  return { ...values, [deriveTo.sourceKey]: [...rows, row] };
}

/**
 * 파생 행 투영(순수) — 원 소스(hazards)의 현재 상태를 파생 소스(사진 카드)에 단방향 반영한다.
 * 파생 행마다:
 * - 원 행(sourceRowId)이 사라졌거나 그 행의 사진 URL이 파생 URL과 다르면(사진 삭제·교체 잔재)
 *   파생 행 **제거**.
 * - 유지되면 자식 항목(findings) = **같은 URL을 가진 원 소스 행 전부**의 fills 값(빈 행 제외,
 *   행 순서 유지 — spill 행 포함)을 itemFields 형상으로 투영.
 * 파생 아닌 행(sourceRowField 없음 — 점검 유래 사진)은 건드리지 않는다.
 */
export function projectDerivedRows(
  values: Record<string, unknown>,
  rule: PhotoAnalysisRule,
): Record<string, unknown> {
  const deriveTo = rule.config.deriveTo;
  if (!deriveTo) return values;
  const photoRows = asRows(values[deriveTo.sourceKey]);
  if (photoRows.length === 0) return values;
  const sourceRows = asRows(values[rule.sourceKey]);
  const fills = rule.config.fills;
  const item = (row: Record<string, unknown>): Record<string, unknown> | null => {
    const description = fills.description ? strOf(row[fills.description]) : '';
    const measure = fills.measuresJoined ? strOf(row[fills.measuresJoined]) : '';
    if (description === '' && measure === '') return null;
    const it: Record<string, unknown> = { rowId: row.rowId };
    if (deriveTo.itemFields) {
      it[deriveTo.itemFields.description] = description;
      it[deriveTo.itemFields.measures] = measure === '' ? [] : [measure];
    }
    return it;
  };
  let changed = false;
  const next: Record<string, unknown>[] = [];
  for (const pr of photoRows) {
    const sourceRowId = pr[deriveTo.sourceRowField];
    if (typeof sourceRowId !== 'string' || sourceRowId === '') {
      next.push(pr); // 파생 아닌 행(점검 유래) — 불변
      continue;
    }
    const url = strOf(pr[deriveTo.urlField]);
    const origin = sourceRows.find((r) => r.rowId === sourceRowId);
    if (!origin || strOf(origin[rule.urlField]) !== url) {
      changed = true; // 원 행 삭제·사진 삭제/교체 잔재 — 파생 제거
      continue;
    }
    if (!deriveTo.findingsField) {
      next.push(pr);
      continue;
    }
    const items = sourceRows
      .filter((r) => strOf(r[rule.urlField]) === url)
      .map(item)
      .filter((it): it is Record<string, unknown> => it !== null);
    const prevItems = pr[deriveTo.findingsField];
    if (JSON.stringify(prevItems) === JSON.stringify(items)) {
      next.push(pr);
    } else {
      changed = true;
      next.push({ ...pr, [deriveTo.findingsField]: items });
    }
  }
  if (!changed) return values;
  return { ...values, [deriveTo.sourceKey]: next };
}

/** 체크 전파 대상 변경 1건. */
export interface CheckboxChange {
  rowId: string;
  /** 변경 후 값(boolean으로 정규화) */
  value: boolean;
  /** 변경 후 행(짝 키 viaField 판독용) */
  row: Record<string, unknown>;
}

/** 이전/다음 행 배열 대조로 체크 필드 변경 행을 찾는다(rowId 보유 행만). */
export function detectCheckboxChanges(
  prevValue: unknown,
  nextValue: unknown,
  field: string,
): CheckboxChange[] {
  const prevById = new Map<string, Record<string, unknown>>();
  for (const row of asRows(prevValue)) {
    if (typeof row.rowId === 'string') prevById.set(row.rowId, row);
  }
  const out: CheckboxChange[] = [];
  for (const row of asRows(nextValue)) {
    const rowId = typeof row.rowId === 'string' ? row.rowId : undefined;
    if (!rowId) continue;
    const prev = prevById.get(rowId);
    if (!prev) continue; // 새 행 — 전이 아님(시드 값)
    if (prev[field] === row[field]) continue;
    out.push({ rowId, value: row[field] !== false && row[field] !== 'false', row });
  }
  return out;
}

/**
 * 체크 전파 적용(순수) — 짝 키 동등(내 행 viaField === 대상 행 targetViaField)인 대상 소스 행에
 * targetField(미지정=내 필드명)를 쓰고, mirrorSiblings면 같은 짝 키의 내 소스 형제 행에도 같은
 * 값을 쓴다(변경 행 자신 포함 — 이미 같은 값이라 무해).
 */
export function applyRowSync(
  values: Record<string, unknown>,
  rule: RowSyncRule,
  change: CheckboxChange,
): Record<string, unknown> {
  const via = strOf(change.row[rule.config.viaField]);
  if (via === '') return values; // 짝 키 없음(손 추가 미업로드 행 등) — 전파 대상 없음
  const targetField = rule.config.targetField ?? rule.field;
  let next = values;
  const targetRows = asRows(values[rule.config.sourceKey]);
  if (targetRows.some((r) => strOf(r[rule.config.targetViaField]) === via)) {
    next = {
      ...next,
      [rule.config.sourceKey]: targetRows.map((r) =>
        strOf(r[rule.config.targetViaField]) === via ? { ...r, [targetField]: change.value } : r,
      ),
    };
  }
  if (rule.config.mirrorSiblings) {
    const ownRows = asRows(next[rule.sourceKey]);
    if (ownRows.some((r) => strOf(r[rule.config.viaField]) === via && r[rule.field] !== change.value)) {
      next = {
        ...next,
        [rule.sourceKey]: ownRows.map((r) =>
          strOf(r[rule.config.viaField]) === via && r[rule.field] !== change.value
            ? { ...r, [rule.field]: change.value }
            : r,
        ),
      };
    }
  }
  return next;
}

/**
 * 저장 payload에서 분석 진행 상태 행 필드(`statusField` — 비예약 UI 상태 키)를 걷어 낸다
 * (순수·불변). 분석이 도는 동안 저장이 일어나도 content에 진행 문구가 잔존하지 않게 하는
 * 마지막 방어선이다(정상 흐름에서는 훅이 종료 시 키를 제거한다).
 * 변경이 없으면 **원본 참조 그대로** 반환한다(불필요한 payload 재조립 방지).
 */
export function stripPhotoAnalysisTransientFields(
  values: Record<string, unknown>,
  rules: PhotoAnalysisRule[],
  statusField: string,
): Record<string, unknown> {
  let next: Record<string, unknown> | null = null;
  for (const rule of rules) {
    const rows: unknown = (next ?? values)[rule.sourceKey];
    if (!Array.isArray(rows)) continue;
    if (!rows.some((r) => r !== null && typeof r === 'object' && statusField in r)) continue;
    const stripped: Record<string, unknown>[] = (rows as Record<string, unknown>[]).map((r) => {
      if (r === null || typeof r !== 'object' || !(statusField in r)) return r;
      const copy = { ...r };
      delete copy[statusField];
      return copy;
    });
    next = { ...(next ?? values), [rule.sourceKey]: stripped };
  }
  return next ?? values;
}
